import { useState, useEffect, useMemo, useRef } from 'react';
import { analyticsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCellValue, formatNumber } from '@/lib/utils';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
  formatTrendTick,
  formatTrendTooltipLabel,
  getTrendXAxisInterval,
} from '@/lib/chartTheme';
import { Users, UserCheck, TrendingUp, Globe, Activity } from 'lucide-react';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { PageSkeleton } from '@/components/PageSkeleton';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { KpiCard as StatCard } from '@/components/KpiCard';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { RegionalMapView, normalizeRegionName } from '@/components/RegionalMapView';
import { TrendRangeTabs, getTrendRangeLabel } from '@/components/TrendRangeTabs';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { EmptyState } from '@/components/EmptyState';
import { UserAvatar } from '@/components/UserAvatar';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

function formatDate(value) {
  if (!value) return formatCellValue(value);
  return new Date(value).toLocaleDateString();
}

function formatTableNumber(value) {
  return value === null || value === undefined ? formatCellValue(value) : formatNumber(value);
}

function statusClass(status) {
  switch (status) {
    case 'Active':
      return 'bg-success/10 text-success border-success/20';
    case 'Light':
      return 'bg-info/10 text-info border-info/20';
    case 'Dormant':
      return 'bg-warning/10 text-warning border-warning/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

const STATUS_ORDER = {
  Active: 0,
  Light: 1,
  Dormant: 2,
  'Never used': 3,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_COLORS = {
  Active: '#22c55e',
  Light: '#3b82f6',
  Dormant: '#f59e0b',
  'Never used': '#94a3b8',
};

const DEPARTMENT_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6'];

const REGIONAL_PERFORMANCE_COLUMNS = [
  { key: 'region', label: 'Region', width: '40%', filterable: true },
  { key: 'active_users', label: 'Active Users', align: 'center', width: '30%' },
  { key: 'conversations', label: 'Conversations', align: 'center', width: '30%' },
];

const COUNTRY_HIGHLIGHT_COLUMNS = [
  { key: 'country', label: 'Country', width: '34%' },
  { key: 'region', label: 'Region', width: '26%', filterable: true },
  { key: 'active_users', label: 'Active Users', align: 'center', width: '20%' },
  { key: 'conversations', label: 'Conversations', align: 'center', width: '20%' },
];

function getAdoptionSummarySortValue(row, key) {
  if (['active_users', 'conversations'].includes(key)) {
    return Number(row[key] || 0);
  }
  return String(row[key] || '').toLowerCase();
}

function UserUsageCard({ row }) {
  const userIdentity = {
    name: row.user_name || row.user_email,
    email: row.user_email,
    avatar_url: row.avatar_url,
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <UserAvatar user={userIdentity} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">{row.user_name || row.user_email}</p>
            <p className="truncate text-sm text-muted-foreground">{row.user_email}</p>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-1 text-xs font-medium ${statusClass(row.adoption_status)}`}>
          {row.adoption_status}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div>
          <p className="text-xs text-muted-foreground">Department</p>
          <p className="font-medium text-foreground">{formatCellValue(row.department)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Conversations</p>
          <p className="font-medium text-foreground">{formatTableNumber(row.conversations)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Active days</p>
          <p className="font-medium text-foreground">{formatTableNumber(row.active_days)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">First active</p>
          <p className="font-medium text-foreground">{formatDate(row.first_active)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Last active</p>
          <p className="font-medium text-foreground">{formatDate(row.last_active)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Location</p>
          <p className="font-medium text-foreground">{formatCellValue([row.region, row.country].filter(Boolean).join(' • '))}</p>
        </div>
      </div>
    </div>
  );
}

function AdoptionSummaryTable({
  columns,
  rows,
  itemLabel,
  searchPlaceholder,
  emptyTitle,
  emptyDescription,
}) {
  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState('all');
  const [sortKey, setSortKey] = useState(columns[0]?.key || 'region');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filterColumn = columns.find((column) => column.filterable);
  const query = searchValue.trim().toLowerCase();

  const filterOptions = useMemo(() => (
    filterColumn
      ? [...new Set(rows.map((row) => row[filterColumn.key]).filter(Boolean))].sort()
      : []
  ), [filterColumn, rows]);

  const filteredRows = useMemo(() => (
    rows
      .filter((row) => {
        const matchesSearch = !query || columns.some((column) => (
          String(row[column.key] || '').toLowerCase().includes(query)
        ));
        const matchesFilter = !filterColumn || filterValue === 'all' || row[filterColumn.key] === filterValue;
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => {
        const aValue = getAdoptionSummarySortValue(a, sortKey);
        const bValue = getAdoptionSummarySortValue(b, sortKey);
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      })
  ), [columns, filterColumn, filterValue, query, rows, sortDirection, sortKey]);

  const totalPages = Math.max(Math.ceil(filteredRows.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(['active_users', 'conversations'].includes(key) ? 'desc' : 'asc');
    }
    setPage(1);
  };

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Globe}
        title={emptyTitle}
        description={emptyDescription}
        variant="panel"
        compact
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder={searchPlaceholder}
        searchTestId={`${itemLabel}-search`}
        className="border-0 bg-transparent p-0"
      >
        {filterColumn && (
          <Select
            value={filterValue}
            onValueChange={(value) => {
              setFilterValue(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder={`All ${filterColumn.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {filterColumn.label.toLowerCase()}</SelectItem>
              {filterOptions.map((option) => (
                <SelectItem key={option} value={option}>{normalizeRegionName(option)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] table-fixed data-table">
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead className="bg-muted/70">
            <tr className="border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`p-3 ${column.align === 'center' ? 'text-center' : 'text-left'}`}
                >
                  <DataTableSortHeader
                    label={column.label}
                    sortKey={column.key}
                    activeSortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    align={column.align}
                    className={column.align ? 'w-full' : undefined}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, index) => (
              <tr key={`${row.country || row.region}-${index}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`p-3 ${column.align === 'center' ? 'text-center tabular-nums' : ''} ${
                      column.key === 'country' || column.key === 'region' ? 'font-medium text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {column.key === 'region'
                      ? normalizeRegionName(formatCellValue(row[column.key]))
                      : ['active_users', 'conversations'].includes(column.key)
                        ? formatTableNumber(row[column.key])
                        : formatCellValue(row[column.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <EmptyState
            icon={Globe}
            title={`No matching ${itemLabel}`}
            description="Try changing your search or filter."
            variant="plain"
            compact
          />
        )}
      </div>

      <DataTablePagination
        page={currentPage}
        totalPages={totalPages}
        totalItems={filteredRows.length}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(1);
        }}
        itemLabel={itemLabel}
        pageSizeTestId={`${itemLabel}-page-size`}
      />
    </div>
  );
}

export default function UsagePage() {
  const [data, setData] = useState(null);
  const [userUsage, setUserUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowLoading, setWindowLoading] = useState(true);
  const [window, setWindow] = useState('30d');
  const [userSearch, setUserSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('adoption_status');
  const [sortDirection, setSortDirection] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('users');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [detailsViewMode, setDetailsViewMode] = useState('list');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      setWindowLoading(true);
      try {
        const [result, usersResult] = await Promise.all([
          analyticsAPI.getUsage(window),
          analyticsAPI.getUsageUsers(window).catch(() => ({ users: [] })),
        ]);
        if (cancelled) return;
        setData(result);
        setUserUsage(usersResult?.users || []);
      } catch (err) {
        console.error('Failed to fetch usage data:', err);
      } finally {
        if (!cancelled) {
          hasLoadedRef.current = true;
          setLoading(false);
          setWindowLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [window]);

  const departments = useMemo(() => (
    [...new Set(userUsage.map((row) => row.department).filter(Boolean))].sort()
  ), [userUsage]);

  const filteredUserUsage = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const filtered = userUsage.filter((row) => {
      const matchesSearch = !query
        || (row.user_name || '').toLowerCase().includes(query)
        || (row.user_email || '').toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all' || row.adoption_status === statusFilter;
      const matchesDepartment = departmentFilter === 'all' || row.department === departmentFilter;
      return matchesSearch && matchesStatus && matchesDepartment;
    });

    const getValue = (row) => {
      if (sortKey === 'adoption_status') return STATUS_ORDER[row.adoption_status] ?? 99;
      if (sortKey === 'first_active') return row.first_active ? new Date(row.first_active).getTime() : 0;
      if (sortKey === 'last_active') return row.last_active ? new Date(row.last_active).getTime() : 0;
      if (sortKey === 'user_name') return (row.user_name || row.user_email || '').toLowerCase();
      return Number(row[sortKey] || 0);
    };

    return [...filtered].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return (a.user_email || '').localeCompare(b.user_email || '');
    });
  }, [departmentFilter, sortDirection, sortKey, statusFilter, userSearch, userUsage]);

  const engagementMix = useMemo(() => (
    Object.keys(STATUS_ORDER).map((status) => ({
      name: status,
      value: userUsage.filter((row) => row.adoption_status === status).length,
    })).filter((item) => item.value > 0)
  ), [userUsage]);

  const departmentAdoption = useMemo(() => {
    const departmentsByName = userUsage.reduce((acc, row) => {
      const department = row.department || 'Unassigned';
      if (!acc[department]) {
        acc[department] = {
          department,
          questions: 0,
          answers: 0,
          conversations: 0,
          active_users: 0,
        };
      }
      acc[department].questions += Number(row.questions || 0);
      acc[department].answers += Number(row.answers || 0);
      acc[department].conversations += Number(row.conversations || 0);
      if (Number(row.conversations || 0) > 0 || Number(row.questions || 0) > 0) {
        acc[department].active_users += 1;
      }
      return acc;
    }, {});

    return Object.values(departmentsByName)
      .sort((a, b) => b.questions - a.questions || b.conversations - a.conversations)
      .slice(0, 6);
  }, [userUsage]);

  const totalQuestions = userUsage.reduce((sum, row) => sum + Number(row.questions || 0), 0);
  const totalAnswers = userUsage.reduce((sum, row) => sum + Number(row.answers || 0), 0);
  const responseCoverage = totalQuestions > 0 ? Math.round((totalAnswers / totalQuestions) * 100) : 0;

  const totalPages = Math.max(Math.ceil(filteredUserUsage.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = filteredUserUsage.length ? (currentPage - 1) * pageSize : 0;
  const pagedUserUsage = filteredUserUsage.slice(startIndex, startIndex + pageSize);

  const updateSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(['conversations', 'active_days', 'first_active', 'last_active'].includes(key) ? 'desc' : 'asc');
    }
  };

  const resetUserTablePage = () => setPage(1);

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="people-adoption"
        kpis={4}
        tabs
        table
        sections={1}
        sectionClassName="h-80"
        data-testid="adoption-page-loading"
      />
    );
  }

  const { executive_snapshot, regional_performance, country_highlights, trend } = data || {};

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8 px-4 sm:px-6 lg:px-8 animate-fade-in" data-testid="adoption-page">
      {/* Page Banner */}
      <PageBanner pageKey="people-adoption" />
      
      <PageHeader title="Adoption" description="User engagement and platform usage metrics" />

      {/* Executive Snapshot - MAU/WAU/DAU */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">Active Users</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="MAU"
            value={formatNumber(executive_snapshot?.mau)}
            subtitle="Monthly Active Users"
            icon={Users}
          />
          <StatCard
            title="WAU"
            value={formatNumber(executive_snapshot?.wau)}
            subtitle="Weekly Active Users"
            icon={UserCheck}
          />
          <StatCard
            title="DAU"
            value={formatNumber(executive_snapshot?.dau)}
            subtitle="Daily Active Users"
            icon={Activity}
          />
          <StatCard
            title="Stickiness"
            value={`${executive_snapshot?.stickiness || 0}%`}
            subtitle="DAU/MAU ratio"
            icon={TrendingUp}
          />
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar
          tabs={[
            { value: 'users', label: 'Details', icon: Users },
            { value: 'trends', label: 'Trends', icon: TrendingUp },
            { value: 'regions', label: 'World', icon: Globe },
          ]}
        />

        <TabsContent value="users" className="space-y-4">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Users className="h-5 w-5 text-info" />
                Usage by User
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Search, filter, and compare adoption activity by employee.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <DataTableToolbar
                searchValue={userSearch}
                onSearchChange={(value) => {
                  setUserSearch(value);
                  resetUserTablePage();
                }}
                searchPlaceholder="Search users..."
                searchTestId="usage-user-search"
                className="border-0 bg-transparent p-0"
              >
                  <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); resetUserTablePage(); }}>
                    <SelectTrigger className="w-full sm:w-40" data-testid="usage-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Light">Light</SelectItem>
                      <SelectItem value="Dormant">Dormant</SelectItem>
                      <SelectItem value="Never used">Never used</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={departmentFilter} onValueChange={(value) => { setDepartmentFilter(value); resetUserTablePage(); }}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="usage-department-filter">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All departments</SelectItem>
                      {departments.map((department) => (
                        <SelectItem key={department} value={department}>{department}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ViewModeToggle value={detailsViewMode} onChange={setDetailsViewMode} />
              </DataTableToolbar>

              {filteredUserUsage.length > 0 ? (
                <>
                  {detailsViewMode === 'card' ? (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      {pagedUserUsage.map((row) => (
                        <UserUsageCard key={row.user_email} row={row} />
                      ))}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px] data-table">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="pb-3 pr-4 text-left text-muted-foreground">
                              <DataTableSortHeader label="User" sortKey="user_name" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} align="left" />
                            </th>
                            <th className="pb-3 pr-4 text-left text-muted-foreground">Department</th>
                            <th className="pb-3 pr-4 text-center text-muted-foreground">
                              <DataTableSortHeader label="Conversations" sortKey="conversations" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                            </th>
                            <th className="pb-3 pr-4 text-center text-muted-foreground">
                              <DataTableSortHeader label="Active Days" sortKey="active_days" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                            </th>
                            <th className="pb-3 pr-4 text-center text-muted-foreground">
                              <DataTableSortHeader label="First Active" sortKey="first_active" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                            </th>
                            <th className="pb-3 pr-4 text-center text-muted-foreground">
                              <DataTableSortHeader label="Last Active" sortKey="last_active" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                            </th>
                            <th className="pb-3 text-center text-muted-foreground">
                              <DataTableSortHeader label="Status" sortKey="adoption_status" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedUserUsage.map((row) => (
                            <tr key={row.user_email} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-3 pr-4">
                                <div className="flex items-center gap-3">
                                  <UserAvatar
                                    user={{
                                      name: row.user_name || row.user_email,
                                      email: row.user_email,
                                      avatar_url: row.avatar_url,
                                    }}
                                    size="sm"
                                  />
                                  <div className="min-w-0">
                                    <p className="truncate font-medium text-foreground">{row.user_name || row.user_email}</p>
                                    <p className="truncate text-xs text-muted-foreground">{row.user_email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 pr-4">
                                <div>
                                  <p className="text-foreground">{formatCellValue(row.department)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatCellValue([row.region, row.country].filter(Boolean).join(' • '))}
                                  </p>
                                </div>
                              </td>
                              <td className="py-3 pr-4 text-center">{formatTableNumber(row.conversations)}</td>
                              <td className="py-3 pr-4 text-center">{formatTableNumber(row.active_days)}</td>
                              <td className="py-3 pr-4 text-center">{formatDate(row.first_active)}</td>
                              <td className="py-3 pr-4 text-center">{formatDate(row.last_active)}</td>
                              <td className="py-3 text-center">
                                <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${statusClass(row.adoption_status)}`}>
                                  {row.adoption_status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <DataTablePagination
                    page={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredUserUsage.length}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
                    itemLabel=""
                    pageSizeTestId="usage-page-size"
                  />
                </>
              ) : (
                <EmptyState
                  icon={Users}
                  title="No user adoption data found"
                  description="Employee adoption will appear here once people start using Eva."
                  variant="panel"
                  compact
                />
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Globe className="h-5 w-5 text-info" />
                Regional Performance
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Search, filter, and compare adoption by region.
              </p>
            </CardHeader>
            <CardContent>
              <AdoptionSummaryTable
                columns={REGIONAL_PERFORMANCE_COLUMNS}
                rows={regional_performance || []}
                itemLabel="regions"
                searchPlaceholder="Search regions..."
                emptyTitle="No regional usage yet"
                emptyDescription="Regional adoption will appear once employee conversations include region details."
              />
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Globe className="h-5 w-5 text-info" />
                Country Highlights
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Search, filter, and compare adoption by country.
              </p>
            </CardHeader>
            <CardContent>
              <AdoptionSummaryTable
                columns={COUNTRY_HIGHLIGHT_COLUMNS}
                rows={country_highlights || []}
                itemLabel="countries"
                searchPlaceholder="Search countries..."
                emptyTitle="No country usage yet"
                emptyDescription="Country adoption will appear once employee conversations include country details."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-card border-border shadow-sm lg:col-span-2">
              <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-lg font-semibold text-foreground">
                  Usage Trend ({getTrendRangeLabel(window)})
                </CardTitle>
                <TrendRangeTabs value={window} onChange={setWindow} className="w-full sm:w-auto sm:min-w-[320px]" />
              </CardHeader>
              <CardContent>
                {windowLoading ? (
                  <Skeleton className="h-72 w-full rounded-xl" data-testid="usage-trend-loading" />
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid {...commonCartesianGridProps} />
                        <XAxis
                          dataKey="date"
                          {...commonXAxisProps}
                          interval={getTrendXAxisInterval(window)}
                          tickFormatter={(value) => formatTrendTick(value, window)}
                        />
                        <YAxis {...commonYAxisProps} />
                        <Tooltip
                          contentStyle={chartTooltipStyle}
                          labelFormatter={(value) => formatTrendTooltipLabel(value, window)}
                        />
                        <Legend {...commonLegendProps} />
                        <Line type="monotone" dataKey="conversations" stroke="#3b82f6" strokeWidth={2} dot={false} name="Conversations" />
                        <Line type="monotone" dataKey="active_users" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Daily Active Users" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">Response Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex h-72 flex-col justify-center gap-5">
                  <div>
                    <p className="text-5xl font-bold text-foreground">{responseCoverage}%</p>
                    <p className="mt-1 text-sm text-muted-foreground">Answers generated for employee questions</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Questions</span>
                      <span className="font-medium text-foreground">{formatNumber(totalQuestions)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Answers</span>
                      <span className="font-medium text-foreground">{formatNumber(totalAnswers)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-success" style={{ width: `${Math.min(responseCoverage, 100)}%` }} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">Engagement Mix</CardTitle>
              </CardHeader>
              <CardContent>
                {windowLoading ? (
                  <Skeleton className="min-h-72 w-full rounded-xl" data-testid="usage-engagement-mix-loading" />
                ) : (
                  <div className="grid min-h-72 grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px] md:items-center">
                    <div className="h-64">
                      {engagementMix.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={engagementMix}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={90}
                              dataKey="value"
                              nameKey="name"
                              paddingAngle={3}
                            >
                              {engagementMix.map((entry) => (
                                <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={chartTooltipStyle} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <EmptyState
                          icon={Activity}
                          title="No engagement data yet"
                          description="Engagement mix will appear as employee usage grows."
                          variant="plain"
                          compact
                        />
                      )}
                    </div>
                    <div className="space-y-3">
                      {engagementMix.map((item) => (
                        <div key={item.name} className="flex items-center justify-between gap-3 text-sm">
                          <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.name] }} />
                            {item.name}
                          </span>
                          <span className="font-semibold text-foreground">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">Department Adoption</CardTitle>
              </CardHeader>
              <CardContent>
                {windowLoading ? (
                  <Skeleton className="h-72 w-full rounded-xl" data-testid="usage-department-adoption-loading" />
                ) : (
                  <div className="h-72">
                    {departmentAdoption.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={departmentAdoption} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid {...commonCartesianGridProps} />
                          <XAxis dataKey="department" {...commonXAxisProps} />
                          <YAxis {...commonYAxisProps} />
                          <Tooltip contentStyle={chartTooltipStyle} />
                          <Legend {...commonLegendProps} />
                          <Bar dataKey="questions" name="Questions" radius={[4, 4, 0, 0]}>
                            {departmentAdoption.map((entry, index) => (
                              <Cell key={entry.department} fill={DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length]} />
                            ))}
                          </Bar>
                          <Bar dataKey="answers" name="Answers" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyState
                        icon={Users}
                        title="No department usage data yet"
                        description="Department adoption will appear after employees begin asking questions."
                        variant="plain"
                        compact
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="regions" className="space-y-6">
          <RegionalMapView
            title="Adoption World Map"
            description="Regional adoption by active users and conversations"
            regions={(regional_performance || []).map((region) => ({
              region: normalizeRegionName(region.region),
              value: region.active_users,
              secondaryValue: region.conversations,
            }))}
            countries={(country_highlights || []).map((country) => ({
              country: country.country,
              region: normalizeRegionName(country.region),
              value: country.active_users,
              secondaryValue: country.conversations,
            }))}
            metricLabel="Active users"
            secondaryLabel="Conversations"
            colorScale="green"
            emptyMessage="Regional adoption will appear here once employee conversations include country or region details."
            timeRangeValue={window}
            onTimeRangeChange={setWindow}
            loading={windowLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
